// Stoic-inspired micro-anchors, reflection prompts, and the article body.
// Everything here is authored in modern, gender-neutral language. No quotes,
// no heavy philosophy, no Latin — just small, actionable prompts a modern
// reader can carry into a day.

/**
 * A short daily "principle" line. Rotates by day-of-year so everyone on the
 * same date sees the same anchor and it changes softly every 24 hours.
 */
export const STOIC_PRINCIPLES = [
  { title: "What you control",        body: "Focus your effort where you have influence today. Let the rest be." },
  { title: "The steady response",     body: "You cannot always choose the moment. You can always choose how you meet it." },
  { title: "One aligned action",      body: "One small aligned choice, made on purpose, is worth more than a good intention held all day." },
  { title: "The clear mirror",        body: "Notice one habit you would not defend if someone asked you why. Notice, without shame." },
  { title: "Simple over loud",        body: "The strongest life is often the quietest. Do less, but do it with more care." },
  { title: "Discipline as care",      body: "Discipline is not punishment. It is the way you take care of the person you are becoming." },
  { title: "Slow the reaction",       body: "Between the trigger and your response, place a single breath. That breath is where your character lives." },
  { title: "Kind, not soft",          body: "You can be firm about what matters and still be kind about how you deliver it." },
  { title: "Enough, on purpose",      body: "Ask yourself what would be genuinely enough today — then choose that instead of more." },
  { title: "Own the moment",          body: "You are not obligated to react to every provocation. Silence is also a decision." },
  { title: "Character in small acts", body: "How you treat a small task quietly rehearses how you will meet a larger one." },
  { title: "Present, not perfect",    body: "Return your attention to what is in front of you. Perfection is a story; presence is the practice." },
  { title: "Choose the response",     body: "Feelings arrive uninvited. What you do with them is entirely yours." },
  { title: "Steady over urgent",      body: "Urgency is loud. Steadiness is what actually gets you there. Choose steadiness today." },
  { title: "The internal witness",    body: "Watch yourself with the same fairness you would give a friend. No harsher, no softer." },
];

/**
 * Journal reflection prompts rooted in Stoic principles — modern phrasing,
 * gender-neutral, practical rather than philosophical. Members can pull one
 * fresh with the "Stoic prompt" button on Journal.
 */
export const STOIC_PROMPTS = [
  "What is one thing today that is inside your control — and one thing that is not? Name each honestly.",
  "Where did you spend energy today on something you cannot actually change?",
  "Describe a moment today when you paused before reacting. What did the pause make possible?",
  "What is one small habit you would keep, even if no one ever noticed?",
  "If today were repeated exactly as it was, would you feel proud of the person you were? Why or why not?",
  "Where were you kind today, and where were you honest? Did those two ever conflict?",
  "Name one thing you complained about today. What would it look like to accept it, or to actually change it?",
  "What is one thing you own that adds calm to your life, and one that adds noise? What would it feel like to keep only the first?",
  "Describe a moment today when you kept a small promise to yourself. What did it cost you? What did it give back?",
  "If someone you respected watched your last hour on autopilot, what would they gently point out?",
  "What is one difficulty you are avoiding, and what would the first small step toward it look like?",
  "Where did you place your identity today — in a title, an opinion, an outcome? What if you set that down for an hour?",
  "Name one thing you assumed about someone today. What if the opposite were also true?",
  "What would 'enough' look like tomorrow — in work, in food, in your body, in your rest?",
  "Where did you rehearse the future today instead of living the present? What did it cost you?",
  "What is a fear that quietly shaped a decision today? What would you decide differently without it?",
  "Describe a moment today when your reaction was bigger than the situation asked for. What was underneath it?",
  "If your body were a person you were caring for, would you say yes to what you gave it today? What would you change?",
  "What is one thing you are grateful for that you would still want, even if you had to earn it again?",
  "Where can you shorten the distance today between what you say you value and what you actually do?",
];

/**
 * Deterministic per-day pick so the "principle of the day" surface holds
 * steady all day and refreshes on its own overnight.
 */
export function principleOfTheDay(iso) {
  let d;
  try { d = iso ? new Date(iso) : new Date(); if (isNaN(d.getTime())) d = new Date(); }
  catch { d = new Date(); }
  const start = new Date(d.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((d - start) / 86400000);
  return STOIC_PRINCIPLES[dayOfYear % STOIC_PRINCIPLES.length];
}

/**
 * Pick a fresh Stoic prompt, avoiding the currently-shown one if provided.
 * Returns just the prompt string.
 */
export function pickStoicPrompt(currentPrompt = "") {
  const pool = STOIC_PROMPTS.filter((p) => p !== currentPrompt);
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * The full article body. Kept intentionally short and practical.
 * Section-based so the article component can render it with clean type
 * hierarchy. `body` supports simple paragraphs; `bullets` renders as a list.
 */
export const STOIC_ARTICLE = {
  title: "Stoicism: A Guide to Self-Mastery and Intentional Living",
  intro:
    "Stoicism is a practical way of thinking, not a set of rules. It teaches that a steady life is built from small, repeatable choices — how you respond, what you attend to, and where you place your effort. It is calm, modern, and quietly useful.",
  sections: [
    {
      heading: "The Core Principles Simplified",
      bullets: [
        "Focus on what you control — your attention, your effort, your response. Spending energy anywhere else is a slow leak; spending it here compounds.",
        "Accept what you don't — outcomes, other people, the weather of a day. Acceptance is not agreement; it is the release of a fight you were never going to win.",
        "Choose the response, not just the reaction — a single breath is often enough. That breath is where your character actually lives.",
        "Act on what you value, not what you feel — feelings pass; values steady. Motivation is a mood; values are a home you keep returning to.",
        "Live in the present — the past is data, the future is a story. Only the moment in front of you is real enough to work with.",
        "Practice self-honesty without self-punishment — clarity, not shame. See yourself the way you would see a friend: fairly, and without a case to prove.",
        "Treat discipline as care — for the person you are becoming. Every small kept promise is you quietly voting for the next version of yourself.",
      ],
    },
    {
      heading: "Be the author of your life",
      body:
        "These four move you from reacting to your life to authoring it. Read them slowly. They compound.",
      subsections: [
        {
          title: "Control vs. influence vs. acceptance",
          body:
            "Three circles, not one. Some things you fully control — your attention, your effort, your response. Some you can influence but not command — relationships, outcomes, how other people receive you. Some you can only accept — the past, other minds, weather, the body's timing. Most suffering comes from confusing the three. Self-awareness grows the moment you stop trying to control what you cannot, and stop passively accepting what you actually can. When you place a situation into the right circle and act only where it belongs, you become far less reactive and far more effective.",
        },
        {
          title: "Virtue as alignment",
          body:
            "Virtue here is not moralism. It is alignment — the felt sense of your daily actions matching what you actually value. When they match, you feel steady even in hard moments. When they don't, no comfort will settle you. Alignment quietly answers the question 'is this me?' as you move through the day and gives you a compass more reliable than mood. It is how you stop performing a life and begin living one.",
        },
        {
          title: "Discipline as freedom",
          body:
            "Discipline is not restriction. It is what removes the constant noise of small decisions and returns your energy to what matters. Someone who negotiates with themselves every morning about whether to move, eat well, or rest spends their agency on maintenance. Discipline is the pre-decision that frees you from re-deciding — and freedom is exactly what it creates. Instead of willpower, you gain attention. Instead of drama, you gain time.",
        },
        {
          title: "Emotional neutrality",
          body:
            "Neutrality is not detachment. It is the ability to feel something fully without letting it hijack your action. You can be angry and still be kind. You can be afraid and still act. Neutrality is built by naming a feeling, letting it move through you, and choosing your response from a slightly wider seat than the first reaction. Over time, this widens who you get to be under pressure — and pressure is where character is actually formed.",
        },
      ],
      closing:
        "Together, these four make you more self-aware — you notice sooner — and more proactive — you act sooner, from a place you have chosen instead of a place that arrived on its own.",
    },
    {
      heading: "Why it supports your becoming journey",
      body:
        "Becoming the next version of yourself is not a dramatic event. It is a series of small, repeatable choices made under real conditions. Stoicism strengthens the interior conditions that make those choices possible — the ground you stand on while everything else moves.",
      bullets: [
        "Emotional regulation — you learn to feel a wave fully without being pulled under by it. Feelings become information, not instructions.",
        "Stress reduction — attention shifts away from what you cannot change, which lowers the daily load on your nervous system and returns energy to the parts of your life that actually respond.",
        "Consistency — small promises kept quietly rewire self-trust. The person who reliably shows up for themselves eventually becomes someone they can rely on.",
        "Discipline — the practice lives in the moment the feeling is gone. Discipline is not force; it is a way of caring for the person you are becoming.",
        "Inner stability — a steady center means fewer decisions are dictated by mood. You act from your values instead of your weather.",
        "Self-trust — self-criticism is replaced by clarity. Slowly, you begin to feel like your own home.",
      ],
    },
    {
      heading: "How to apply it daily",
      bullets: [
        "Begin with a one-minute question: what is inside my control today?",
        "Between a trigger and your reply, place one breath. Then decide.",
        "Keep one small promise to yourself each day — the same one, for a week.",
        "At night, ask what you would keep and what you would change. Write neither for long.",
        "Notice a complaint. Ask whether to accept it or to actually change it. Choose one.",
        "Give yourself permission to do less on purpose. Enough is a decision.",
      ],
    },
    {
      heading: "A few Stoic lines to sit with",
      body:
        "These were written by different people, in different centuries, to themselves — not to be read. They still hold. Read one slowly. Let it sit.",
      quotes: [
        { text: "You have power over your mind — not outside events. Realize this, and you will find strength.", author: "Marcus Aurelius" },
        { text: "It is not what happens to you, but how you react to it that matters.", author: "Epictetus" },
        { text: "We suffer more in imagination than in reality.", author: "Seneca" },
        { text: "The impediment to action advances action. What stands in the way becomes the way.", author: "Marcus Aurelius" },
        { text: "First say to yourself what you would be; and then do what you have to do.", author: "Epictetus" },
        { text: "Difficulties strengthen the mind, as labor does the body.", author: "Seneca" },
        { text: "Well-being is realized by small steps, but is truly no small thing.", author: "Zeno of Citium" },
        { text: "Confine yourself to the present.", author: "Marcus Aurelius" },
      ],
    },
  ],
  // 3–5 reflection prompts the reader can carry into their journal.
  reflection_prompts: [
    "What is one thing inside your control today that you have been ignoring?",
    "Where has reacting cost you more than responding would have?",
    "What is one habit you would keep, even if no one ever saw you do it?",
    "If today were a rehearsal for the person you are becoming, what would you keep — and what would you release?",
    "What would 'enough' look like tomorrow, and how would that change your morning?",
  ],
};
